import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GOAL_RESULTS, GOAL_PROMPTS, NO_PROMPT_RESULTS } from "../lib/goalMeta";
import { Chip, StatusBadge, EmptyState } from "./fields";
import { Target, ChevronLeft, X } from "lucide-react";
import { toast } from "sonner";

const TYPE_TONE = { "Classroom/Academic": "info", Behavior: "warning", Adaptive: "success", Clinician: "neutral" };

export default function GoalCheckSheet({ student, onClose, onSaved }) {
  const { user } = useAuth();
  const [goals, setGoals] = useState(null);
  const [goal, setGoal] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({ result: "", prompt_level: "", trials_correct: "", trials_total: "", note: "" });
  const [myRecent, setMyRecent] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/goals?student_id=${student.id}`).then((r) => setGoals(r.data.filter((g) => g.status === "Active"))).catch(() => setGoals([])); }, [student.id]);
  useEffect(() => {
    if (!goal) return;
    api.get(`/goal-checks?goal_id=${goal.id}`).then((r) => setMyRecent(r.data.slice(0, 5))).catch(() => {});
  }, [goal, saving]);

  const noPrompt = NO_PROMPT_RESULTS.includes(form.result);
  const pct = form.trials_correct !== "" && form.trials_total !== "" && Number(form.trials_total) > 0
    ? Math.round((Number(form.trials_correct) / Number(form.trials_total)) * 100) : null;
  const trialsOk = (form.trials_correct === "" && form.trials_total === "") ||
    (form.trials_correct !== "" && form.trials_total !== "" && Number(form.trials_total) > 0 &&
      Number(form.trials_correct) >= 0 && Number(form.trials_correct) <= Number(form.trials_total));
  const valid = form.result && (noPrompt || form.prompt_level) && (form.note || "").length <= 300 && trialsOk;

  const reset = () => { setForm({ result: "", prompt_level: "", trials_correct: "", trials_total: "", note: "" }); setEditing(null); };

  const save = async () => {
    setSaving(true);
    const body = {
      goal_id: goal.id, result: form.result, prompt_level: noPrompt ? "" : form.prompt_level,
      trials_correct: form.trials_correct === "" ? null : Number(form.trials_correct),
      trials_total: form.trials_total === "" ? null : Number(form.trials_total),
      note: form.note,
    };
    try {
      if (editing) await api.put(`/goal-checks/${editing}`, body);
      else await api.post("/goal-checks", body);
      toast.success(editing ? "Entry updated" : "Goal check recorded");
      reset();
      setGoal(null);
      setGoals(null);
      api.get(`/goals?student_id=${student.id}`).then((r) => setGoals(r.data.filter((g) => g.status === "Active")));
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ result: c.result, prompt_level: c.prompt_level || "", trials_correct: c.trials_correct ?? "", trials_total: c.trials_total ?? "", note: c.note || "" });
  };

  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="sf-sheet bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-[#E5E9EE] flex items-center gap-2.5 z-10">
          {goal && (
            <button data-testid="goal-check-back-btn" onClick={() => { setGoal(null); reset(); }} className="p-1.5 -ml-1.5 text-[#667085] hover:text-[#132238] rounded-lg"><ChevronLeft size={18} /></button>
          )}
          <Target size={17} className="text-[#087F5B]" />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-[#132238] text-[15px] truncate">Goal Check — {student.first_name || student.name}</h3>
            {goal && <p className="text-[11px] text-[#667085] truncate">{goal.area} · {goal.goal_type}</p>}
          </div>
          <button data-testid="goal-check-close-btn" onClick={onClose} className="p-1.5 text-[#667085] hover:text-[#132238] rounded-lg"><X size={17} /></button>
        </div>

        {!goal ? (
          <div className="p-5 space-y-2.5">
            {goals === null && <p className="text-sm text-[#667085]">Loading goals…</p>}
            {goals?.length === 0 && <EmptyState icon={Target} title="No active goals yet" hint="Goals are added by your TAG Supervisor or Special Education Teacher." />}
            {goals?.map((g) => (
              <button key={g.id} data-testid={`goal-item-${g.id}`} onClick={() => setGoal(g)}
                className="w-full text-left border border-[#E5E9EE] rounded-xl p-3.5 hover:bg-[#F7F9FA] transition-colors duration-150">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[#132238]">{g.area}</p>
                  <StatusBadge tone={TYPE_TONE[g.goal_type]}>{g.goal_type}</StatusBadge>
                  {g.due && <span data-testid={`goal-due-${g.id}`} className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">DUE</span>}
                </div>
                <p className={`text-xs text-[#667085] mt-1 ${expanded[g.id] ? "" : "line-clamp-2"}`}>
                  {g.text}
                </p>
                {g.target && <p className="text-[11px] text-[#98A2B3] mt-1">Target: {g.target}</p>}
              </button>
            ))}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="rounded-xl bg-[#F7F9FA] border border-[#E5E9EE] p-3">
              <p className={`text-xs text-[#667085] ${expanded[goal.id] ? "" : "line-clamp-3"}`}>{goal.text}</p>
              <button onClick={() => setExpanded((x) => ({ ...x, [goal.id]: !x[goal.id] }))} className="text-[11px] font-semibold text-[#087F5B] mt-1">
                {expanded[goal.id] ? "Show less" : "Show full goal"}
              </button>
              {goal.target && <p className="text-[11px] text-[#98A2B3] mt-1">Target: {goal.target}</p>}
            </div>

            <div>
              <p className="text-xs font-bold tracking-[0.06em] uppercase text-[#667085] mb-2">Result *</p>
              <div className="flex flex-wrap gap-1.5">
                {GOAL_RESULTS.map((r) => (
                  <Chip key={r} testId={`result-${r.toLowerCase().replace(/\s+/g, "-")}`} selected={form.result === r}
                    onClick={() => setForm({ ...form, result: r })}>{r}</Chip>
                ))}
              </div>
            </div>

            {!noPrompt && (
              <div>
                <p className="text-xs font-bold tracking-[0.06em] uppercase text-[#667085] mb-2">Prompt level *</p>
                <div className="flex flex-wrap gap-1.5">
                  {GOAL_PROMPTS.map((p) => (
                    <Chip key={p} testId={`prompt-${p.toLowerCase()}`} selected={form.prompt_level === p}
                      onClick={() => setForm({ ...form, prompt_level: p })}>{p}</Chip>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold tracking-[0.06em] uppercase text-[#667085]">Trials correct</label>
                <input data-testid="trials-correct-input" type="number" min="0" inputMode="numeric" value={form.trials_correct}
                  onChange={(e) => setForm({ ...form, trials_correct: e.target.value })}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.06em] uppercase text-[#667085]">Trials total</label>
                <input data-testid="trials-total-input" type="number" min="0" inputMode="numeric" value={form.trials_total}
                  onChange={(e) => setForm({ ...form, trials_total: e.target.value })}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
              </div>
            </div>
            {pct != null && trialsOk && <p data-testid="trials-pct" className="text-sm font-semibold text-[#087F5B]">= {pct}%</p>}
            {form.trials_correct !== "" && form.trials_total !== "" && !trialsOk && (
              <p data-testid="trials-error" className="text-xs font-medium text-red-600">Trials correct must be between 0 and trials total.</p>
            )}

            <div>
              <label className="text-xs font-bold tracking-[0.06em] uppercase text-[#667085]">Note (optional)</label>
              <textarea data-testid="goal-check-note-input" rows={2} maxLength={300} value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
              <p className="text-[11px] text-[#98A2B3] text-right">{(form.note || "").length}/300</p>
            </div>

            <button data-testid="save-goal-check-btn" onClick={save} disabled={!valid || saving}
              className="w-full bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] py-3 text-sm transition-colors duration-150 disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Update entry" : "Save goal check"}
            </button>

            {myRecent.length > 0 && (
              <div className="pt-1">
                <p className="text-xs font-bold tracking-[0.06em] uppercase text-[#667085] mb-2">My recent entries</p>
                <div className="space-y-1.5">
                  {myRecent.map((c) => (
                    <div key={c.id} data-testid={`my-check-${c.id}`} className="flex items-center gap-2 text-xs border border-[#E5E9EE] rounded-lg px-2.5 py-2">
                      <span className="font-mono text-[#98A2B3]">{c.date}</span>
                      <span className="font-semibold text-[#132238]">{c.result}</span>
                      {c.prompt_level && <span className="text-[#667085]">· {c.prompt_level}</span>}
                      {c.pct != null && <span className="text-[#667085]">· {c.pct}%</span>}
                      {(c.aide_id === user?.id && (c.date === today || ["supervisor", "tag_admin"].includes(user?.role))) && (
                        <button data-testid={`edit-check-${c.id}`} onClick={() => startEdit(c)}
                          className="ml-auto font-semibold text-[#087F5B] hover:underline">Edit</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
